/**
 * 页面事件参数采集器
 * 采集当前页面 URL/title/referrer 等信息
 */

import type { LeaveType, PageEventParams, PageParams } from '../types/event'
import type { ReferrerResolver, RoutePathResolver } from '../types/config'
import { localStorageAdapter } from '../utils/storage'
import { DATA_ATTRS } from '../core/constants'

// 首次访问标记
const FIRST_VISIT_KEY = 'xt_tracker_first_visit'

// SPA 模式下上一页面 URL（用于 referrerPage 采集）
let spaLastPageUrl: string = ''

/**
 * 更新 SPA 模式下的上一页面 URL
 * 应在路由变化时调用，传入变化前的 URL
 * @param url 上一页面的完整 URL
 */
export function updateSpaLastPageUrl(url: string): void {
  spaLastPageUrl = url
}

/**
 * 获取当前 SPA 上一页面 URL（主要用于测试）
 */
export function getSpaLastPageUrl(): string {
  return spaLastPageUrl
}

/**
 * 解析当前页面的 referrerPage
 * 优先级：spaLastPageUrl > document.referrer > referrerResolver()
 * @param referrerResolver 外部 referrer 解析回调（可选）
 * @returns referrerPage 字符串，无来源时返回空字符串
 */
export function resolveReferrerPage(referrerResolver?: ReferrerResolver): string {
  if (spaLastPageUrl) return spaLastPageUrl

  const docReferrer = typeof document !== 'undefined' ? document.referrer : ''
  if (docReferrer) return docReferrer

  if (referrerResolver) {
    try {
      return referrerResolver() || ''
    } catch {
      // 静默失败
    }
  }
  return ''
}

/**
 * 解析 routePath：优先用 resolver，失败/未配置走兜底
 * @param resolver 路由路径解析回调
 * @returns 标准化路由路径
 */
export function resolveRoutePath(resolver?: RoutePathResolver): string {
  if (resolver) {
    try {
      const routeInfo = resolver()
      if (routeInfo && typeof routeInfo.path === 'string' && routeInfo.path) {
        return routeInfo.path
      }
    } catch {
      // 静默失败，走兜底
    }
  }
  return getDefaultRoutePath()
}

/**
 * 获取兜底 routePath（未配 resolver 时使用）
 */
function getDefaultRoutePath(): string {
  if (typeof location === 'undefined') return ''
  // hash 模式：#/product/1?tab=info → /product/1
  if (location.hash.startsWith('#/')) {
    const hash = location.hash.slice(1)
    return hash.split('?')[0] || '/'
  }
  return location.pathname
}

/**
 * 采集页面事件参数
 * @param routePath 标准化路由路径（由调用方通过 resolveRoutePath 解析后传入）
 * @param referrerResolver 外部 referrer 解析回调（可选）
 * @returns PageEventParams 对象
 */
export function collectPageEvent(
  routePath?: string,
  referrerResolver?: ReferrerResolver
): PageEventParams {
  const isFirstVisit = checkFirstVisit()

  const referrerPage = resolveReferrerPage(referrerResolver)

  return {
    referrerPage,
    currentUrl: typeof location !== 'undefined' ? location.href : '',
    pageTitle: typeof document !== 'undefined' ? document.title : '',
    pageId: getPageId(routePath),
    routePath,
    routeMode: detectRouteMode(),
    isFirstVisit,
  }
}

/**
 * 采集页面销毁事件参数
 * @param enterTime 页面进入时间戳
 * @param leaveType 离开类型
 * @param targetPage 目标页面
 * @param currentUrl 当前页面 URL
 * @param isOperation 是否为用户操作
 * @param routePath 标准化路由路径
 * @param referrerResolver 外部 referrer 解析回调（可选）
 * @returns PageEventParams 对象
 */
export function collectPageLeaveEvent(
  enterTime: number,
  leaveType?: LeaveType,
  targetPage?: string,
  currentUrl?: string,
  isOperation?: boolean,
  routePath?: string,
  referrerResolver?: ReferrerResolver
): PageEventParams {
  const baseParams = collectPageEvent(routePath, referrerResolver)

  return {
    ...baseParams,
    stayDuration: Date.now() - enterTime,
    leaveType: leaveType,
    targetPage: targetPage || '',
    currentUrl: currentUrl || '',
    isOperation: isOperation ?? false,
  }
}

/**
 * 检查是否首次访问
 */
function checkFirstVisit(): boolean {
  const visited = localStorageAdapter.getItem(FIRST_VISIT_KEY)
  if (!visited) {
    localStorageAdapter.setItem(FIRST_VISIT_KEY, 'true')
    return true
  }
  return false
}

/**
 * 获取页面 ID（有 routePath 时基于它生成，否则走 location.pathname）
 * @param routePath 标准化路由路径
 */
export function getPageId(routePath?: string): string {
  return routePath || (typeof location !== 'undefined' ? location.pathname : '')
}

/**
 * 检测路由模式
 */
function detectRouteMode(): string {
  if (typeof location === 'undefined') return 'unknown'

  // 检测是否为 hash 路由
  if (location.hash.startsWith('#/')) {
    return 'hash'
  }

  // 默认为 history 模式
  return 'history'
}

// ── 页面级参数采集 ──

/**
 * 解析 location.search 为查询参数对象
 */
function parseQueryParams(): Record<string, string> {
  if (typeof location === 'undefined') return {}
  const params: Record<string, string> = {}
  const searchParams = new URLSearchParams(location.search)
  searchParams.forEach((value, key) => {
    params[key] = value
  })
  return params
}

/**
 * 解析页面中所有 data-tracker-page-custom-params 属性值并合并为一个对象
 * 多个元素的属性值按 DOM 顺序合并，后者覆盖前者
 */
function parseCustomParams(): Record<string, unknown> {
  if (typeof document === 'undefined') return {}
  const result: Record<string, unknown> = {}
  const elements = document.querySelectorAll(`[${DATA_ATTRS.PAGE_CUSTOM_PARAMS}]`)
  elements.forEach((el) => {
    const attrValue = el.getAttribute(DATA_ATTRS.PAGE_CUSTOM_PARAMS)
    if (!attrValue) return
    try {
      const parsed = JSON.parse(attrValue)
      // 仅接受非 null 的 plain object
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        Object.assign(result, parsed)
      }
    } catch {
      // 解析失败静默忽略
    }
  })
  return result
}

/**
 * 采集页面级参数
 * @returns PageParams 对象，包含 query（query查询参数/params路由参数）和 custom（自定义参数）
 */
export function collectPageParams(routePathResolver?: RoutePathResolver): PageParams {
  const currentRoute: any = routePathResolver ? routePathResolver() : { params: {} }
  return {
    query: parseQueryParams(),
    params: currentRoute?.params || {},
    custom: parseCustomParams(),
  }
}
